import moment from 'moment';
import React from 'react';
import { CSVLink } from 'react-csv';
import { Button, Icon } from 'semantic-ui-react';
// eslint-disable-next-line import/no-unresolved
import { SemanticCOLORS } from 'semantic-ui-react/dist/commonjs/generic';
import { RidershipStopAggRecord } from '../../../types/ridershipTypes';
import type { DayTypeFilter } from '../../../utils/dayType';
import { toRidershipCsvInteger } from './ridershipUtils';

export interface RidershipExportButtonProps {
    rows: RidershipStopAggRecord[] | null | undefined;
    stopSeatCapacityMap?: Map<string, number>;
    isLoading?: boolean;
    disabled?: boolean;
    aggregateMode: 'sum' | 'avg' | 'avg_trips';
    context?: {
        routeId?: string;
        routeLabel?: string;
        directionId?: string;
        directionLabel?: string;
        tripId?: string;
        fromDate?: string;
        toDate?: string;
        dayType?: DayTypeFilter;
        timeRangeStart?: string;
        timeRangeEnd?: string;
    };
    triggerButtonProps?: {
        color?: SemanticCOLORS;
        basic?: boolean;
        floated?: 'right' | 'left';
        size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large' | 'huge' | 'massive';
    };
}

const headers = [
    { label: 'Stop ID', key: 'stopId' },
    { label: 'Stop Name', key: 'stopName' },
    { label: 'Stop Sequence', key: 'stopSequence' },
    { label: 'Boardings', key: 'boarding' },
    { label: 'Alightings', key: 'alighting' },
    { label: 'Load', key: 'load' },
    { label: 'Seats', key: 'seats' },
    { label: 'Seat Utilization %', key: 'seatUtilizationPercent' },
    { label: 'Route ID', key: 'routeId' },
    { label: 'Route', key: 'routeLabel' },
    { label: 'Direction ID', key: 'directionId' },
    { label: 'Direction', key: 'directionLabel' },
    { label: 'Trip ID', key: 'tripId' },
    { label: 'From Date', key: 'fromDate' },
    { label: 'To Date', key: 'toDate' },
    { label: 'Time Range Start', key: 'timeRangeStart' },
    { label: 'Time Range End', key: 'timeRangeEnd' },
    { label: 'Day Type', key: 'dayType' },
    { label: 'Aggregate', key: 'aggregateMode' },
];

function toSeatUtilizationPercent(load?: number | null, seats?: number | null) {
    if (!seats || seats <= 0) return undefined;
    return Math.trunc(((load ?? 0) / seats) * 100);
}

function toAggregateModeLabel(mode: 'sum' | 'avg' | 'avg_trips') {
    if (mode === 'avg') return 'Daily Average';
    if (mode === 'avg_trips') return 'Average Trips';
    return 'Sum';
}

const RidershipExportButton: React.FC<RidershipExportButtonProps> = ({
    rows,
    stopSeatCapacityMap,
    isLoading,
    disabled,
    aggregateMode,
    context,
    triggerButtonProps,
}) => {
    const linkRef = React.useRef<any>(null);
    const csvRows = React.useMemo(() => {
        if (!rows || rows.length === 0) return [];
        return rows.map(row => {
            const stopKey = String(row.stopId ?? '');
            const seats = stopSeatCapacityMap?.get(stopKey);
            const seatUtilizationPercent = toSeatUtilizationPercent(row.load, seats);
            return {
                stopId: stopKey,
                stopName: row.stopName ?? '',
                stopSequence: row.stopSequence ?? '',
                boarding: toRidershipCsvInteger(row.boarding),
                alighting: toRidershipCsvInteger(row.alighting),
                load: toRidershipCsvInteger(row.load),
                seats: toRidershipCsvInteger(seats),
                seatUtilizationPercent: seatUtilizationPercent ?? '',
                routeId: context?.routeId ?? '',
                routeLabel: context?.routeLabel ?? '',
                directionId: context?.directionId ?? '',
                directionLabel: context?.directionLabel ?? '',
                tripId: context?.tripId ?? '',
                fromDate: context?.fromDate ?? '',
                toDate: context?.toDate ?? '',
                timeRangeStart: context?.timeRangeStart ?? '',
                timeRangeEnd: context?.timeRangeEnd ?? '',
                dayType: context?.dayType ?? '',
                aggregateMode: toAggregateModeLabel(aggregateMode),
            };
        });
    }, [rows, stopSeatCapacityMap, context, aggregateMode]);

    const fileName = React.useMemo(() => {
        const routePart = context?.routeId ? `_${context.routeId}` : '';
        const directionPart = context?.directionId ? `_${context.directionId}` : '';
        const timestamp = moment(new Date()).format('YYYY_MM_DD');
        return `average_weekday_activity_by_stop${routePart}${directionPart}_${timestamp}.csv`;
    }, [context?.routeId, context?.directionId]);

    const isDownloadDisabled = disabled || isLoading || !rows || rows.length === 0;

    return (
        <>
                <div style={{ display: 'inline-block', marginBottom: '12px' }}>
                    <CSVLink
                        data={csvRows}
                        headers={headers}
                        filename={fileName}
                        target="_blank"
                        className="hidden"
                        ref={linkRef as any}
                    />
                    <Button
                        color={triggerButtonProps?.color ?? 'grey'}
                        basic={triggerButtonProps?.basic ?? true}
                        floated={triggerButtonProps?.floated}
                        size={triggerButtonProps?.size ?? 'small'}
                        icon
                        labelPosition="left"
                        loading={isLoading}
                        disabled={isDownloadDisabled}
                        onClick={() => {
                            linkRef.current?.link?.click();
                        }}
                    >
                        <Icon name="download" />
                        Download CSV
                    </Button>
                </div>
        </>
    );
};

export default RidershipExportButton;
